import { message } from 'antd';
import { useEffect, useState } from 'react';

import { useI18nContext } from '@/i18n/I18n';
import {
  getMerchantSecretInfo,
  submitMerchantInfo,
} from '@/services/oauthAdmin';
import { IParentPermission } from '@/services/types/apiManagement';
import {
  emailValidateNew,
  hrefValidate,
  isValidIP,
  preSql,
} from '@/utils/ruleValidate';

import { IApiDetail } from './comps/CreateApiForm';
import { calPermission, permissionMapKey } from './helper';
import { IBaseInput, tabType } from './type';

export const useOauthAdmin = () => {
  const [$t] = useI18nContext();
  const [activeTab, setActiveTab] = useState(tabType.CONFIG_APP);
  const [appName, setAppName] = useState('');
  const [email, setEmail] = useState('');
  const [homepageUrl, setHomepageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [tosUrl, setTosUrl] = useState('');
  const [checked, setChecked] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [underSubmission, setUnderSubmission] = useState(false);
  const [inEditMode, setInEditMode] = useState(false);
  const [hasAuth, setHasAuth] = useState(true);
  const [apiDetail, setApiDetail] = useState<IApiDetail>({
    readOnly: true,
    bindIps: '',
    permissions: [],
  });

  const baseData: IBaseInput[] = [
    {
      key: 'name',
      val: appName,
      title: $t('oauthAdmin.clientName'),
      placeholder: $t('oauthAdmin.clientName'),
      handleChange: (key: string) => {
        return (val: string) => {
          setAppName(val);
        };
      },
      required: true,
    },
    {
      key: 'email',
      val: email,
      title: $t('oauthAdmin.userEmail'),
      placeholder: $t('oauthAdmin.userEmailPlaceholder'),
      handleChange: (key: string) => {
        return (val: string) => {
          setEmail(val);
        };
      },
      required: true,
    },
  ];

  const netDomainData: IBaseInput[] = [
    {
      key: 'homepageUrl',
      val: homepageUrl,
      title: 'redirect_uri',
      placeholder: $t('oauthAdmin.appHomePagePlaceholder'),
      handleChange: (key: string) => {
        return (val: string) => {
          setHomepageUrl(val);
        };
      },
      required: true,
    },
    {
      key: 'privacyPolicyUrl',
      val: privacyPolicyUrl,
      title: $t('oauthAdmin.privacyUrl'),
      placeholder: $t('oauthAdmin.privacyUrlPlaceholder'),
      handleChange: (key: string) => {
        return (val: string) => {
          setPrivacyPolicyUrl(val);
        };
      },
      required: true,
    },
    {
      key: 'tosUrl',
      val: tosUrl,
      title: $t('oauthAdmin.termOfServiceUrl'),
      placeholder: $t('oauthAdmin.termOfServiceUrlPlaceholder'),
      handleChange: (key: string) => {
        return (val: string) => {
          setTosUrl(val);
        };
      },
    },
  ];

  const handleSubmit = (openApi: {
    readOnly: boolean;
    bindIps: string;
    permissions: { [key: string]: string[] };
  }) => {
    if (!appName) {
      message.error($t('oauthAdmin.clientNameRequired'));
      return;
    } else if (!email) {
      message.error($t('oauthAdmin.userEmailRequired'));
      return;
    } else if (email !== '' && emailValidateNew(email).status === false) {
      message.error($t('oauthAdmin.userEmailValidate'));
      return;
    } else if (logoUrl === '') {
      message.error($t('oauthAdmin.appLogoRequired'));
      return;
    } else if (!homepageUrl) {
      message.error($t('oauthAdmin.appHomePageRequired'));
      return;
    } else if (
      homepageUrl !== '' &&
      homepageUrl.split(',').some((url) => !hrefValidate(url).status)
    ) {
      message.error('Link format is incorrect');
      return;
    } else if (!privacyPolicyUrl) {
      message.error($t('oauthAdmin.privacyUrlRequired'));
      return;
    } else if (
      privacyPolicyUrl !== '' &&
      hrefValidate(privacyPolicyUrl).status === false
    ) {
      message.error('Link format is incorrect');
      return;
    } else if (tosUrl !== '' && hrefValidate(tosUrl).status === false) {
      message.error('Link format is incorrect');
      return;
    } else if (!openApi?.permissions) {
      message.error($t('Please set the permission parameters for the key'));
      return;
    }
    setUnderSubmission(true);
    submitMerchantInfo({
      client_name: appName,
      logo_url: logoUrl,
      redirect_uri: homepageUrl,
      scope: checked
        ? 'openapi,restrict,restrict-email'
        : 'restrict,restrict-email', // 多个使用逗号分隔
      ...(checked && { ips: '' }), // 多个ip使用逗号分隔
      client_email: email,
      privacy_url: privacyPolicyUrl, // 隐私政策链接
      service_url: tosUrl, // 服务条款链接
      api_detail: {
        permissions: openApi.permissions,
        read_only: openApi.readOnly,
      }, // api权限信息
    })
      .then((res) => {
        if (inEditMode) {
          message.success($t('submit_successfully'));
        } else if (res?.result?.client_id) {
          setClientId(res.result.client_id);
          setClientSecret(res.result.client_secret);
          setShowDetailModal(true);
        }
      })
      .catch((error) => {
        // msgError(error);
        message.error(error?.ret_msg || 'server error');
      })
      .finally(() => {
        setUnderSubmission(false);
      });
  };

  const formatApiDetail = (apiDetail: {
    ips: string;
    read_only: boolean;
    permissions: IParentPermission;
  }) => {
    const bindIps =
      apiDetail?.ips?.toString() === '*' ? '' : apiDetail?.ips?.toString();
    const readOnly = !!apiDetail?.read_only;
    const permissions = permissionMapKey(apiDetail?.permissions || [], false);
    setApiDetail({
      readOnly,
      bindIps,
      permissions,
    });
  };

  useEffect(() => {
    getMerchantSecretInfo()
      .then((res) => {
        setHasAuth(true);
        if (res?.result?.status === 0) {
          // 已创建商户信息
          setAppName(res.result.client_name);
          setEmail(res.result.client_email);
          setHomepageUrl(res.result.redirect_uri);
          if (res?.result?.scope?.includes('openapi')) {
            setChecked(true);
          }
          setPrivacyPolicyUrl(res.result?.privacy_url);
          setTosUrl(res.result?.service_url);
          setLogoUrl(res.result?.logo_url);
          setInEditMode(true);
          formatApiDetail({
            ...(res.result.api_detail || {}),
            ips: res.result?.ips === '*' ? '' : res.result?.ips,
          });
        }
      })
      .catch((error) => {
        // ret_code 50004 没有权限
        message.error(error.ret_msg || 'no auth');
        setHasAuth(false);
      });
  }, []);

  return {
    baseData,
    netDomainData,
    activeTab,
    checked,
    showDetailModal,
    appName,
    clientId,
    clientSecret,
    apiDetail,
    hasAuth,
    logoUrl,
    $t,
    setLogoUrl,
    setChecked,
    setActiveTab,
    setShowDetailModal,
    handleSubmit,
    setApiDetail,
    formatApiDetail,
    underSubmission,
    inEditMode,
  };
};
